/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	OnInit,
	ChangeDetectionStrategy,
	Input,
	OnDestroy,
	ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { CalendarWorkdays, GroupedTicket } from '@touchplan/ticket';
import { DateUtils } from 'src/app/utils/date-utils';
import * as appStrings from '@touchplan/strings';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService } from '@touchplan/logging';

export interface IChipData {
	dayName: string;
	date: number;
	month: string;
	year: number;
	colorNgClass: string[];
	enabled: boolean;
}

/**
 * The component that visually represents a series of workdays and non-workdays.
 *
 * WARNING: Enter at your own risk!
 * ---------------------------------
 * If you plan to make any major changes, it might be wiser to rewrite this
 * entirely rather than trying to patch this.
 * Proceed with caution, and may the debugging gods be with you.
 */
@Component({
	// tslint:disable-next-line: component-selector
	selector: 'workdays',
	templateUrl: './workdays.component.html',
	styleUrls: ['./workdays.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: WorkdaysComponent,
		},
		LoggerServiceFactory.provide('WorkdaysComponent'),
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkdaysComponent implements ControlValueAccessor, OnInit, OnDestroy {
	@Input() public liveStart$: Observable<string>;
	@Input() public calendarWorkdays: CalendarWorkdays;
	@Input() public editTicket$: Observable<GroupedTicket>;

	private shutdownSubject$ = new Subject<void>();
	public ticketWorkdayBoolean$ = new BehaviorSubject<boolean[]>([]);
	public label: string;
	public monthAndYear$ = new BehaviorSubject<string>('');
	public nonWorkdaysLabel$ = new BehaviorSubject<string>('');
	public chips$ = new BehaviorSubject<IChipData[]>([]);

	public chipListFormControl: UntypedFormControl;

	// These variables store function callbacks provided by Angular.
	// Their signatures are very generic INTENTIONALLY.
	// See the details in the code for the ControlValueAccessor interface.
	private onValueChange = (thing: any) => {};
	private onTouched = () => {};
	private isDisabled = false;

	constructor(private logger: DefaultLoggerService, private cdr: ChangeDetectorRef) {}

	public ngOnInit(): void {
		this.label = appStrings.WORKDAYS;
		this.chipListFormControl = new UntypedFormControl(null);

		// Create an observable that combines the role/plan non-workdays,
		// with the observable for the ticket's workdays,
		// and which yields the per-day color to use for the <mat-chip>s:
		combineLatest([this.ticketWorkdayBoolean$, this.editTicket$, this.liveStart$])
			.pipe(
				map(([ticketWorkdayBooleans, ticket, liveStart]) => {
					const roleId = ticket.view.liveRole.$id || ticket.view.roleId;

					// Calculate plan/role workday booleans
					// They represent the gray and white day-stripes you see on the
					// Desktop, when (in Settings) you view a particular Role's non-workdays,
					// along with the project's/plan's regular non-workdays/holidays:
					const dayCount = ticketWorkdayBooleans.length;
					const startDate = DateUtils.stringToLocalTimezoneDate(liveStart);

					// Adjust date to calculate the finish date
					const finishDate = new Date(startDate);
					finishDate.setDate(startDate.getDate() + dayCount - 1);
					const finish = DateUtils.dateToStandardStringFormat(finishDate);

					const planRoleWorkdayBooleans = this.calendarWorkdays.calculateWorkdays(liveStart, finish, roleId);

					// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6648 - [ngClass]
					// Calculate color classes for the chips
					const colorNgClasses = this.combineWorkdayBooleansToColorNgClass(
						ticketWorkdayBooleans,
						planRoleWorkdayBooleans
					);
					// END PLACEHOLDER REPLACE AS PART OF TP-6648
					return {
						ticketWorkdayBooleans,
						planRoleWorkdayBooleans,
						colorNgClasses,
						liveStart,
					};
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(({ ticketWorkdayBooleans, planRoleWorkdayBooleans, colorNgClasses, liveStart }) => {
				// Update chips data
				const chipsData = this.updateChipsData(ticketWorkdayBooleans, colorNgClasses, liveStart);
				// Update chips list
				this.chips$.next(chipsData);
				// TP-6722: the chips in the template are not fully rendered when setValue is processed,
				// so we are updating the view before setting the values for the form control
				this.cdr.detectChanges();
				// Update internal form control
				this.updateMatChipListFormControl(ticketWorkdayBooleans, chipsData);
				// Update month and year label
				const monthAndYearLabel = this.monthAndYearString(chipsData);
				this.monthAndYear$.next(monthAndYearLabel);
				// Update non-workdays label
				const nonWorkdaysLabel = this.selectedNonWorkdaysString(
					ticketWorkdayBooleans,
					planRoleWorkdayBooleans,
					liveStart
				);
				this.nonWorkdaysLabel$.next(nonWorkdaysLabel);
			});

		// Convert the <mat-chip-list>'s FormControl value to the boolean array value
		// needed to store into Firebase:
		const ticketsBooleanArrayValue$ = this.chipListFormControl.valueChanges.pipe(
			map((selectedChipsData) => {
				return this.selectedChipsToBooleanArray(selectedChipsData);
			}),
			takeUntil(this.shutdownSubject$)
		);

		// Send those boolean array values "out" to the externally bound FormControl:
		ticketsBooleanArrayValue$.subscribe((booleans) => {
			this.onValueChange(booleans);
		});
	}

	/**
	 * Adds `['white-background-chip']` to the day if the day is not workday
	 * but the day is workday according to the role.;
	 * otherwise, returns an empty array for that index.
	 *
	 * @param {boolean[]} ticketWorkdayBooleans - Project-specific workday statuses.
	 * @param {boolean[]} planRoleWorkdayBooleans - Plan-role-specific workday statuses.
	 * @returns {string[][]} - Array of CSS class arrays for each index.
	 */
	private combineWorkdayBooleansToColorNgClass(
		ticketWorkdayBooleans: boolean[],
		planRoleWorkdayBooleans: boolean[]
	): string[][] {
		return ticketWorkdayBooleans.map((isWorkDay: boolean, index: number): string[] => {
			const planRole: boolean = planRoleWorkdayBooleans[index];
			return !isWorkDay && planRole ? ['white-background-chip'] : [];
		});
	}

	/**
	 * Converts selected chip data to a boolean array indicating which chips are selected.
	 *
	 * @param {IChipData[]} selectedChipsData - The array of chips that are currently selected.
	 * @returns {boolean[]} - A boolean array where each element corresponds to whether a chip in `chips$` is selected.
	 */
	private selectedChipsToBooleanArray(selectedChipsData: IChipData[]): boolean[] {
		const allChipsData: IChipData[] = this.chips$.value;
		return allChipsData.map((chipData: IChipData): boolean => selectedChipsData.includes(chipData));
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6648
	// Not called; chip-lists don't seem to support this.
	// Nonetheless, keeping because it might (?) in the future,
	// but more because the ControlValueAccessor interface
	// requires a registerOnTouched implementation.
	public onBlur(): void {
		this.onTouched();
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-6648

	// ControlValueAccessor function.
	// The FormControl bound "externally" to the <workdays> component should provide an object of
	// type, "boolean[]", to match what's stored in Firebase.
	// This function is called when that externally-bound FormControl has an initial or new boolean[]
	// to provide.
	//
	// The FormControl bound "internally" to the <mat-chip-list> uses a value of
	// type, "ICipData[]", with the array containing the IChipData objects of the selected chips,
	// but excluding the objects of the deselected chips.
	public writeValue(obj: any): void {
		if (obj === null || obj === undefined) {
			obj = [];
		}
		if (!Array.isArray(obj)) {
			this.logger.warn("<workdays> component's FormControl value is not an array", obj);
			return;
		}
		if (obj.some((item) => typeof item !== 'boolean')) {
			this.logger.warn("<workdays> component's FormControl value is not an array of booleans", obj);
			return;
		}
		// cast obj after validation
		this.ticketWorkdayBoolean$.next(obj as boolean[]);
	}

	/**
	 * Updates the form control.
	 */
	private updateMatChipListFormControl(workdaysBooleanArray: boolean[], allChipsData: IChipData[]): void {
		const chipDataArrayResult: IChipData[] = workdaysBooleanArray
			.map((bool, index) => (bool ? allChipsData[index] : null))
			.filter((chip): chip is IChipData => chip !== null);
		this.chipListFormControl.setValue(chipDataArrayResult, { emitEvent: false });
	}

	/**
	 * Updates the chip data for `chips$`.
	 * @returns {IChipData[]} - Array of chip data objects.
	 */
	private updateChipsData(
		workdaysBooleanArray: boolean[],
		colorNgClasses: string[][],
		liveStart: string
	): IChipData[] {
		const currentDate: Date | null = DateUtils.stringToLocalTimezoneDate(liveStart);
		if (!currentDate) {
			return [];
		}
		return workdaysBooleanArray.map((_, index) => {
			const chipData: IChipData = {
				dayName: DateUtils.getShortDayString(currentDate),
				date: currentDate.getDate(),
				month: DateUtils.getMonthString(currentDate),
				year: currentDate.getFullYear(),
				colorNgClass: colorNgClasses[index],
				enabled: index !== 0 && !this.isDisabled,
			};
			currentDate.setDate(currentDate.getDate() + 1);
			return chipData;
		});
	}

	/**
	 * Generates a month and year range string from the array of chip data containing month and year information.
	 */
	private monthAndYearString(chipDataArray: IChipData[]): string {
		if (!chipDataArray || chipDataArray.length === 0) {
			return '';
		}
		const firstChip = chipDataArray[0];
		const firstPair = `${firstChip.month} ${firstChip.year}`;
		if (chipDataArray.length === 1) {
			return firstPair;
		}
		const lastChip = chipDataArray[chipDataArray.length - 1];
		const lastPair = `${lastChip.month} ${lastChip.year}`;
		return firstPair === lastPair ? firstPair : `${firstPair} - ${lastPair}`;
	}

	/**
	 * Generates a string of selected non-workdays based on the ticket workdays, plan role days, and start date.
	 *
	 * @returns {string} - A string representing the selected non-workdays, or an empty string if none are selected.
	 */
	private selectedNonWorkdaysString(
		ticketWorkdayBooleans: boolean[],
		planRoleWorkdayBooleans: boolean[],
		liveStart: string
	): string {
		if (!liveStart || !ticketWorkdayBooleans || ticketWorkdayBooleans.length === 0) {
			return '';
		}
		const currentDate: Date | null = DateUtils.stringToLocalTimezoneDate(liveStart);
		if (!currentDate) {
			return '';
		}
		const dateStrings: string[] = ticketWorkdayBooleans
			.map((ticketWorkday, index) => ticketWorkday && !planRoleWorkdayBooleans[index])
			.map((isSelectedNonWorkday, index) => {
				if (isSelectedNonWorkday) {
					const dateString = `${currentDate.getMonth() + 1}/${currentDate.getDate()}`;
					currentDate.setDate(currentDate.getDate() + index);
					return dateString;
				}
				currentDate.setDate(currentDate.getDate() + index);
				return null;
			})
			.filter((dateString): dateString is string => dateString !== null);
		return dateStrings.length > 0 ? appStrings.NON_WORKDAYS(dateStrings.join(', ')) : '';
	}

	/**
	 * ControlValueAccessor function:
	 * This method is called by Angular to give this class a callback method, which is to be called
	 * when the (externally visible) value of this component should be updated.
	 */
	public registerOnChange(fn: (thing: any) => void): void {
		this.onValueChange = fn;
	}

	/**
	 * ControlValueAccessor function:
	 * This is another method that registers a callback function.  This one needs to be called when this
	 * component is "touched".  We track that via the "(blur)" event handler in the HTML template, which
	 * calls the "onBlur()" method.  That in turn calls the callback method provided as a parameter here.
	 */
	public registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	// ControlValueAccessor function:
	public setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
		const currentChipsData = this.chips$.value;
		currentChipsData.forEach((elem, index) => {
			elem.enabled = index !== 0 && !isDisabled;
		});
		this.chips$.next(currentChipsData);
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
